import { CSRF_HEADER, CSRF_HEADER_VALUE, type ApiErrorBody, type ErrorCode } from '@gn/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'NETWORK',
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
  get isNetwork() {
    return this.code === 'NETWORK';
  }
}

type Listener = () => void;
const unauthListeners = new Set<Listener>();
export function onUnauthenticated(fn: Listener) {
  unauthListeners.add(fn);
  return () => unauthListeners.delete(fn);
}

export interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Do not broadcast 401 (used by the /auth/me probe). */
  quiet401?: boolean;
}

/**
 * Same-origin JSON client. Cookies carry the session (HttpOnly — never readable from JS);
 * every request sends the CSRF header the API requires for mutations.
 */
export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const headers: Record<string, string> = { [CSRF_HEADER]: CSRF_HEADER_VALUE, accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method: opts.method ?? 'GET',
      headers,
      credentials: 'same-origin',
      cache: 'no-store',
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK', 'Network error');
  }
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('content-type') ?? '';
  const data = ct.includes('application/json') ? await res.json().catch(() => null) : null;
  if (!res.ok) {
    const e = (data as ApiErrorBody | null)?.error;
    if (res.status === 401 && !opts.quiet401) unauthListeners.forEach((l) => l());
    if (res.status === 502 || res.status === 503 || res.status === 504) {
      if (!e) throw new ApiError(res.status, 'NETWORK', 'Server unavailable');
    }
    throw new ApiError(res.status, (e?.code ?? 'INTERNAL') as ErrorCode, e?.message ?? res.statusText, e?.details);
  }
  return data as T;
}

export const get = <T>(p: string, signal?: AbortSignal) => api<T>(p, { signal });
export const post = <T>(p: string, body: unknown = {}) => api<T>(p, { method: 'POST', body });
export const put = <T>(p: string, body: unknown) => api<T>(p, { method: 'PUT', body });
export const patch = <T>(p: string, body: unknown) => api<T>(p, { method: 'PATCH', body });
export const del = <T>(p: string) => api<T>(p, { method: 'DELETE' });

/** Download a binary export via a temporary object URL. */
export async function download(path: string, filename: string): Promise<void> {
  const res = await fetch(`/api/v1${path}`, { headers: { [CSRF_HEADER]: CSRF_HEADER_VALUE }, credentials: 'same-origin' });
  if (!res.ok) throw new ApiError(res.status, 'INTERNAL', 'Download failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function idemKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}
